import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import Sidebar from '../components/Sidebar';
import {
  ShieldCheck, Users, Code2, Plus, Trash2, Edit2, Search,
  UserPlus, X, ChevronRight, RefreshCw, Check, Settings
} from 'lucide-react';

const ROLES = ['admin','member','viewer','agent','client'];
const ROLE_DESCS = {
  admin:  'Full access — manage users, projects, assignments',
  member: 'Standard developer access to workspaces',
  viewer: 'Read-only access to API docs',
  agent:  'Handles support tickets, can view assigned projects',
  client: 'External user — accesses assigned projects & raises tickets',
};

export default function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab]         = useState('users');
  const [users, setUsers]     = useState([]);
  const [projects, setProjects] = useState([]);
  const [search, setSearch]   = useState('');
  const [loading, setLoading] = useState(false);
  const [editUser, setEditUser]     = useState(null);
  const [showAssign, setShowAssign] = useState(null); // project being assigned
  const [assignRole, setAssignRole] = useState('client');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [assignments, setAssignments] = useState({});
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name:'', email:'', password:'', role:'client' });

  if (!['admin','member'].includes(user?.role)) {
    navigate('/'); return null;
  }

  useEffect(() => { loadUsers(); loadAllProjects(); }, []);

  async function loadUsers() {
    setLoading(true);
    try { const r = await api.get('/admin/users'); setUsers(r.data); }
    catch {} finally { setLoading(false); }
  }

  async function loadAllProjects() {
    try {
      const ws = await api.get('/workspaces');
      const all = [];
      for (const w of ws.data) {
        const ps = await api.get(`/workspaces/${w.id}/projects`);
        all.push(...ps.data.map(p => ({ ...p, workspace_name: w.name })));
      }
      setProjects(all);
    } catch {}
  }

  async function loadAssignments(projectId) {
    try {
      const r = await api.get(`/projects/${projectId}/assignments`);
      setAssignments(a => ({ ...a, [projectId]: r.data }));
    } catch {}
  }

  async function changeRole(userId, role) {
    try {
      await api.put(`/admin/users/${userId}/role`, { role });
      setUsers(us => us.map(u => u.id === userId ? { ...u, role } : u));
      toast.success('Role updated');
      setEditUser(null);
    } catch { toast.error('Failed to update role'); }
  }

  async function assignUser() {
    if (!selectedUserId || !showAssign) return;
    try {
      await api.post(`/projects/${showAssign}/assignments`, { user_id: selectedUserId, role: assignRole });
      toast.success('User assigned!');
      loadAssignments(showAssign);
      setSelectedUserId('');
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  }

  async function removeAssignment(projectId, userId) {
    if (!window.confirm('Remove this assignment?')) return;
    try {
      await api.delete(`/projects/${projectId}/assignments/${userId}`);
      toast.success('Removed');
      loadAssignments(projectId);
    } catch { toast.error('Failed'); }
  }

  async function inviteUser(e) {
    e.preventDefault();
    try {
      // Admin-only endpoint that honours the chosen role (the public
      // /auth/register endpoint ignores role and would store 'member').
      const res = await api.post('/admin/users', inviteForm);
      toast.success(`User created as ${res.data.role}`);
      setShowInvite(false);
      setInviteForm({ name:'', email:'', password:'', role:'client' });
      loadUsers();
    } catch (err) {
      const msg = err.response?.data?.error
        || err.response?.data?.details?.[0]?.message
        || 'Failed';
      toast.error(msg);
    }
  }

  const filteredUsers = users.filter(u => !search ||
    u.name.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="app-layout">
      <Sidebar />
      <div className="main-content">
        {/* Header */}
        <div style={A.header}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <ShieldCheck size={20} style={{ color:'var(--accent)' }} />
            <h1 style={{ fontSize:20, fontWeight:800 }}>Admin Panel</h1>
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => setShowInvite(true)}>
            <UserPlus size={14}/> Invite User
          </button>
        </div>

        {/* Tabs */}
        <div className="tabs" style={{ padding:'0 24px', background:'var(--bg-surface)', borderBottom:'1px solid var(--border)' }}>
          {[['users','Users & Roles'],['projects','Project Assignments']].map(([id,label]) => (
            <button key={id} className={`tab ${tab===id ? 'active':''}`} onClick={() => setTab(id)}>{label}</button>
          ))}
        </div>

        {/* ── Users tab ── */}
        {tab === 'users' && (
          <div style={A.content}>
            <div style={A.toolbar}>
              <div style={A.searchWrap}>
                <Search size={13} style={{ color:'var(--text-3)' }} />
                <input style={A.searchInput} placeholder="Search users..." value={search} onChange={e => setSearch(e.target.value)} />
              </div>
              <span style={{ fontSize:12, color:'var(--text-3)' }}>{filteredUsers.length} users</span>
            </div>

            <div style={A.grid}>
              {filteredUsers.map(u => (
                <div key={u.id} style={A.userCard}>
                  <div style={A.userTop}>
                    <div className="avatar avatar-md">{u.name[0]?.toUpperCase()}</div>
                    <div style={A.userInfo}>
                      <div style={A.userName}>{u.name}</div>
                      <div style={A.userEmail}>{u.email}</div>
                    </div>
                  </div>
                  <div style={A.userRole}>
                    {editUser === u.id ? (
                      <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                        {ROLES.map(r => (
                          <button key={r} style={{ ...A.roleBtn, background: u.role===r ? 'var(--accent-dim)' : 'var(--bg-hover)', borderColor: u.role===r ? 'var(--accent)' : 'var(--border)', color: u.role===r ? 'var(--accent)' : 'var(--text-2)' }}
                            onClick={() => changeRole(u.id, r)}>
                            {r}
                          </button>
                        ))}
                        <button style={A.cancelBtn} onClick={() => setEditUser(null)}><X size={12}/></button>
                      </div>
                    ) : (
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <span className={`role-chip role-${u.role}`}>{u.role}</span>
                        <p style={{ fontSize:11, color:'var(--text-3)', flex:1 }}>{ROLE_DESCS[u.role]}</p>
                        <button className="btn-icon" style={{ padding:4 }} onClick={() => setEditUser(u.id)} title="Edit role">
                          <Edit2 size={13}/>
                        </button>
                      </div>
                    )}
                  </div>
                  <div style={A.userMeta}>
                    Joined {new Date(u.created_at).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Projects tab ── */}
        {tab === 'projects' && (
          <div style={A.content}>
            {projects.map(p => (
              <div key={p.id} style={A.projectCard}>
                <div style={A.projectHead}>
                  <div>
                    <div style={A.projectName}>{p.name}</div>
                    <div style={{ fontSize:11, color:'var(--text-3)' }}>{p.workspace_name}</div>
                  </div>
                  <div style={{ display:'flex', gap:6 }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/project/${p.id}`)}>
                      <Code2 size={12}/> Open
                    </button>
                    <button className="btn btn-primary btn-sm" onClick={() => { setShowAssign(p.id); loadAssignments(p.id); }}>
                      <UserPlus size={12}/> Assign User
                    </button>
                  </div>
                </div>

                {/* Assignments */}
                <div style={A.assignList}>
                  {(assignments[p.id] || []).map(asg => (
                    <div key={asg.id} style={A.assignRow}>
                      <div className="avatar avatar-sm">{asg.name?.[0]?.toUpperCase()}</div>
                      <div style={A.assignInfo}>
                        <span style={{ fontWeight:600, fontSize:13 }}>{asg.name}</span>
                        <span style={{ fontSize:11, color:'var(--text-3)' }}>{asg.email}</span>
                      </div>
                      <span className={`role-chip role-${asg.role}`}>{asg.role}</span>
                      <span style={{ fontSize:11, color:'var(--text-3)' }}>
                        Assigned {new Date(asg.assigned_at).toLocaleDateString()}
                      </span>
                      <button style={{ background:'none', border:'none', color:'var(--red)', cursor:'pointer', padding:3, display:'flex' }}
                        onClick={() => removeAssignment(p.id, asg.user_id)}>
                        <Trash2 size={13}/>
                      </button>
                    </div>
                  ))}
                  {!(assignments[p.id]?.length) && (
                    <div style={{ fontSize:12, color:'var(--text-3)', padding:'8px 0' }}>
                      No users assigned. Click "Assign User" to grant access.
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Assign User Modal ── */}
      {showAssign && (
        <div className="modal-overlay" onClick={() => setShowAssign(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">Assign User to Project</h3>
            <div className="form-group">
              <label className="form-label">Select User</label>
              <select value={selectedUserId} onChange={e => setSelectedUserId(e.target.value)}>
                <option value="">Choose a user...</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.name} ({u.email}) — {u.role}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Assignment Role</label>
              <select value={assignRole} onChange={e => setAssignRole(e.target.value)}>
                <option value="client">Client — Can test APIs & create tickets</option>
                <option value="agent">Agent — Can handle support tickets</option>
                <option value="member">Member — Full developer access</option>
                <option value="viewer">Viewer — Read-only access</option>
              </select>
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setShowAssign(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={assignUser} disabled={!selectedUserId}>
                <UserPlus size={13}/> Assign
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Invite User Modal ── */}
      {showInvite && (
        <div className="modal-overlay" onClick={() => setShowInvite(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">Create New User</h3>
            <form onSubmit={inviteUser}>
              <div className="form-group">
                <label className="form-label">Full Name *</label>
                <input placeholder="Jane Smith" value={inviteForm.name} onChange={e => setInviteForm(f=>({...f,name:e.target.value}))} required autoFocus />
              </div>
              <div className="form-group">
                <label className="form-label">Email Address *</label>
                <input type="email" placeholder="jane@company.com" value={inviteForm.email} onChange={e => setInviteForm(f=>({...f,email:e.target.value}))} required />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Password *</label>
                  <input type="password" placeholder="Min 8 characters" value={inviteForm.password} onChange={e => setInviteForm(f=>({...f,password:e.target.value}))} required minLength={8} />
                </div>
                <div className="form-group">
                  <label className="form-label">Role</label>
                  <select value={inviteForm.role} onChange={e => setInviteForm(f=>({...f,role:e.target.value}))}>
                    {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              </div>
              {inviteForm.role && (
                <div style={{ fontSize:12, color:'var(--text-3)', background:'var(--bg-elevated)', padding:'8px 12px', borderRadius:7, marginBottom:16 }}>
                  {ROLE_DESCS[inviteForm.role]}
                </div>
              )}
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setShowInvite(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary"><UserPlus size={13}/> Create User</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const A = {
  header: { display:'flex', alignItems:'center', justifyContent:'space-between', padding:'16px 24px', borderBottom:'1px solid var(--border)', background:'var(--bg-surface)', flexShrink:0 },
  content: { flex:1, overflow:'auto', padding:24 },
  toolbar: { display:'flex', alignItems:'center', gap:12, marginBottom:20 },
  searchWrap: { display:'flex', alignItems:'center', gap:8, background:'var(--bg-elevated)', border:'1px solid var(--border-light)', borderRadius:8, padding:'7px 12px', flex:1, maxWidth:360 },
  searchInput: { background:'transparent', border:'none', fontSize:13, padding:0, width:'100%', boxShadow:'none', height:'auto' },
  grid: { display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:14 },
  userCard: { background:'var(--bg-surface)', border:'1px solid var(--border)', borderRadius:12, padding:18, display:'flex', flexDirection:'column', gap:12 },
  userTop: { display:'flex', alignItems:'center', gap:12 },
  userInfo: { flex:1, overflow:'hidden' },
  userName: { fontSize:14, fontWeight:700, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' },
  userEmail: { fontSize:12, color:'var(--text-3)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' },
  userRole: { padding:'10px 0', borderTop:'1px solid var(--border)' },
  userMeta: { fontSize:11, color:'var(--text-3)' },
  roleBtn: { fontSize:11, padding:'3px 9px', borderRadius:99, border:'1px solid', cursor:'pointer', fontFamily:'var(--font)', transition:'all .12s' },
  cancelBtn: { display:'flex', alignItems:'center', background:'none', border:'1px solid var(--border)', borderRadius:99, padding:'3px 7px', color:'var(--text-3)', cursor:'pointer' },
  projectCard: { background:'var(--bg-surface)', border:'1px solid var(--border)', borderRadius:12, padding:18, marginBottom:14 },
  projectHead: { display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:14, gap:12 },
  projectName: { fontSize:15, fontWeight:700, marginBottom:2 },
  assignList: { display:'flex', flexDirection:'column', gap:6 },
  assignRow: { display:'flex', alignItems:'center', gap:10, padding:'8px 12px', background:'var(--bg-elevated)', borderRadius:8 },
  assignInfo: { flex:1, display:'flex', flexDirection:'column', overflow:'hidden' },
};
